import { Search, Filter } from "lucide-react";
import { useState, useEffect, SubmitEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { Category } from "../types/category";
import { Product } from "../types/product";
import dbJson from "../data/db.json";
import { ScrollToTopButton } from "./ui/scrollToTopButton";

export const ProductsPage = () => {
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const location = useLocation();

  // Поиск
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get("category");
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [location.search]);

  // Сетаем данные
  useEffect(() => {
    setAllProducts(dbJson.productsData);
    setCategories(dbJson.categoriesData);
    setLoading(false);
  }, []);

  // Debounce
  useEffect(() => {
    let timer = setTimeout(() => {
      setSearchQuery(inputValue)
    }, 300)

    return () => { clearTimeout(timer) }
  }, [inputValue])

  const handleSearch = (e: SubmitEvent) => {
    e.preventDefault();
  };

  const filteredProducts = allProducts.filter((product) => {
    const matchCategory = selectedCategory
      ? product.category === selectedCategory
      : true;

    const matchSearch = searchQuery
      ? product.name.toLowerCase().includes(searchQuery.toLowerCase())
      : true;

    return matchCategory && matchSearch;
  });

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-slate-50" data-testid="products-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 py-12">
        <div className="container-custom">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 uppercase tracking-tight mb-4">
            Каталог продукции
          </h1>
          <p className="text-slate-600 max-w-2xl">
            Полный спектр оборудования для систем мониторинга и управления
          </p>
        </div>
      </div>

      <div className="container-custom py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <aside className={`lg:w-72 flex-shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white border border-slate-200 rounded-sm p-6 sticky top-24">
              <h3 className="font-semibold text-slate-900 uppercase text-sm tracking-wide mb-4">
                Категории
              </h3>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedCategory("")}
                  className={`w-full text-left px-3 py-2 text-sm rounded-sm transition-colors ${selectedCategory === ""
                    ? "bg-[#0056D2] text-white"
                    : "text-slate-700 hover:bg-slate-100"
                    }`}
                  data-testid="filter-all"
                >
                  Все категории
                </button>
                {categories.map((category: Category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full text-left px-3 py-2 text-sm rounded-sm transition-colors ${selectedCategory === category.id
                      ? "bg-[#0056D2] text-white"
                      : "text-slate-700 hover:bg-slate-100"
                      }`}
                    data-testid={`filter-${category.id}`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Search and Mobile Filter Toggle */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <form onSubmit={handleSearch} className="flex-1 relative">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Поиск по каталогу..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#0056D2]/20 focus:border-[#0056D2]"
                  data-testid="search-input"
                />
              </form>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden flex items-center justify-center gap-2 px-4 py-3 border border-slate-200 rounded-sm text-slate-700"
                data-testid="toggle-filters-btn"
              >
                <Filter size={18} />
                Фильтры
              </button>
            </div>

            {/* Products Count */}
            <p className="text-sm text-slate-500 mb-6">
              Найдено: <span className="font-medium text-slate-900">{filteredProducts.length}</span> товаров
            </p>

            {/* Products Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-80 bg-slate-200 animate-pulse rounded-sm" />
                ))}
              </div>
            ) : allProducts.length === 0 ? (
              <div className="text-center py-16 bg-white border border-slate-200 rounded-sm">
                <p className="text-slate-500">Товары не найдены</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredProducts.map((product, index) => (
                  <Link to={`/products/${product.id}`}>
                    <div
                      key={product.id}
                      className="card-product animate-fade-in"
                      style={{ animationDelay: `${index * 0.05}s` }}
                      data-testid={`product-card-${product.id}`}
                    >
                      <div className="aspect-video bg-slate-100 mb-4 overflow-hidden rounded-sm">
                        <img
                          src={product.image || "https://images.unsplash.com/photo-1518770660439-4636190af475?w=400"}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <p className="text-xs font-mono text-[#0056D2] uppercase tracking-wider mb-2">
                        {product.subcategory}
                      </p>
                      <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2">
                        {product.name}
                      </h3>
                      <p className="text-sm text-slate-600 mb-4 line-clamp-2 flex-1">
                        {product.description}
                      </p>
                      {product.features && product.features.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-auto">
                          {product.features.slice(0, 2).map((feature, i) => (
                            <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-sm">
                              {feature}
                            </span>
                          ))}
                          {product.features.length > 2 && (
                            <span className="text-xs text-slate-400">
                              +{product.features.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>


          <ScrollToTopButton />
        </div>
      </div>
    </div>
  );
};